import { LanguageCode } from '@heidi/schema/ml-scribe';
import { getSelectableDisplayLanguageCodes } from './display-languages';

/**
 * Mapping of country codes to available display languages.
 */
export const countryToDisplayLanguages: Record<string, LanguageCode[]> = {
  // English-speaking countries
  US: [LanguageCode.en],
  GB: [LanguageCode.en],
  AU: [LanguageCode.en],
  NZ: [LanguageCode.en],
  IE: [LanguageCode.en],
  ZA: [LanguageCode.en],
  SG: [LanguageCode.en],
  PH: [LanguageCode.en],
  IN: [LanguageCode.en],
  JM: [LanguageCode.en],
  BB: [LanguageCode.en],
  TT: [LanguageCode.en],
  GH: [LanguageCode.en],
  KE: [LanguageCode.en],
  NG: [LanguageCode.en],
  MT: [LanguageCode.en],

  // German-speaking countries
  DE: [LanguageCode.de],
  AT: [LanguageCode.de],
  CH: [LanguageCode.de, LanguageCode.fr, LanguageCode.it],
  LI: [LanguageCode.de],

  // French-speaking countries
  FR: [LanguageCode.fr],
  BE: [LanguageCode.fr, LanguageCode.nl],
  LU: [LanguageCode.fr, LanguageCode.de],
  MC: [LanguageCode.fr],

  // Bilingual countries (English + other language)
  // English is listed first as the default
  CA: [LanguageCode.en, LanguageCode.fr], // Canada - bilingual (English/French)

  // Spanish-speaking countries
  ES: [LanguageCode.es, LanguageCode.ca],
  MX: [LanguageCode.es],
  AR: [LanguageCode.es],
  CO: [LanguageCode.es],
  CL: [LanguageCode.es],
  PE: [LanguageCode.es],
  VE: [LanguageCode.es],
  EC: [LanguageCode.es],
  GT: [LanguageCode.es],
  CU: [LanguageCode.es],
  BO: [LanguageCode.es],
  DO: [LanguageCode.es],
  HN: [LanguageCode.es],
  PY: [LanguageCode.es],
  SV: [LanguageCode.es],
  NI: [LanguageCode.es],
  CR: [LanguageCode.es],
  PA: [LanguageCode.es],
  UY: [LanguageCode.es],

  // Italian-speaking countries
  IT: [LanguageCode.it],
  SM: [LanguageCode.it],
  VA: [LanguageCode.it],

  // Asian languages
  JP: [LanguageCode.ja],
  CN: [LanguageCode.zh],
  TW: [LanguageCode.zh],
  HK: [LanguageCode.en, LanguageCode.zh],
  MO: [LanguageCode.zh],

  // Nordic, Dutch, Polish, Portuguese languages
  NL: [LanguageCode.nl],
  DK: [LanguageCode.da],
  FI: [LanguageCode.fi, LanguageCode.sv],
  NO: [LanguageCode.no],
  SE: [LanguageCode.sv],
  PL: [LanguageCode.pl],
  PT: [LanguageCode.pt],
  BR: [LanguageCode.pt],
  AD: [LanguageCode.ca, LanguageCode.es],
  KR: [LanguageCode.ko],
};

/**
 * Countries where English is the primary/official language and no other
 * Heidi-supported language is commonly spoken.
 * Users from these countries won't see the language dropdown.
 *
 * Note: Canada is NOT in this list because French is also an official language.
 * Canadian users will see a language dropdown with French as an option.
 */
export const englishSpeakingCountryCodes = new Set([
  'US', // United States
  'GB', // United Kingdom
  'AU', // Australia
  'NZ', // New Zealand
  'IE', // Ireland
  'ZA', // South Africa
  'SG', // Singapore
  'PH', // Philippines
  'IN', // India
  'JM', // Jamaica
  'BB', // Barbados
  'TT', // Trinidad and Tobago
  'GH', // Ghana
  'KE', // Kenya
  'NG', // Nigeria
  'MT', // Malta
]);

/**
 * Get available display languages for a given country code.
 * Only returns languages that are in the supported display languages list.
 */
export function getAvailableDisplayLanguagesForCountry(
  countryCode?: string
): LanguageCode[] {
  if (!countryCode) return [];
  const countryLangs = countryToDisplayLanguages[countryCode] ?? [];
  // Filter to only include languages that are currently enabled
  return countryLangs.filter((lang) =>
    getSelectableDisplayLanguageCodes().includes(lang)
  );
}

/**
 * Check if a country is primarily English-speaking.
 */
export function isEnglishSpeakingCountry(countryCode?: string): boolean {
  return !countryCode || englishSpeakingCountryCodes.has(countryCode);
}

/**
 * Determine if the language dropdown should be shown for a given country.
 * Returns true only if there are 2+ languages available for the country.
 * Countries with 0-1 languages will auto-select without showing a dropdown.
 */
export function shouldShowLanguageDropdown(countryCode?: string): boolean {
  if (!countryCode) return false;
  return getAvailableDisplayLanguagesForCountry(countryCode).length > 1;
}
